import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook to manage carrier selection logic
 * Handles carrier filtering, auto-selection, and Health Analyzer integration
 */
const useCarrierSelection = (activeTab, aaqCarriers = [], alqCarriers = [], formData, updateFormField) => {
  const [hasUserEditedLifeCarriers, setHasUserEditedLifeCarriers] = useState(false);
  const [hasAutoSelectedAnnuity, setHasAutoSelectedAnnuity] = useState(false);
  const [userClearedAnnuity, setUserClearedAnnuity] = useState(false);
  const manualAnnuityEditRef = useRef(false);

  /**
   * Get available carriers based on active tab
   */
  const getAvailableCarriers = useCallback(() => {
    switch (activeTab) {
      case 'new-annuity':
        return aaqCarriers || [];
      case 'new-life':
        return alqCarriers || [];
      case 'new-ltc':
        return [];
      default:
        return [];
    }
  }, [activeTab, aaqCarriers, alqCarriers]);

  /**
   * Get carriers from quote results for filtering
   */
  const getAvailableCarriersFromQuotes = useCallback((quotesByProductType, originalQuotesByProductType) => {
    const dataToUse = quotesByProductType && Object.keys(quotesByProductType).length > 0 ? quotesByProductType : originalQuotesByProductType;

    const allQuotes = Object.values(dataToUse).flat();
    const carriersMap = new Map();

    allQuotes.forEach(quote => {
      const carrierName = quote.carrier_name || quote.carrier || quote.company_name || 'Unknown Carrier';
      const carrierId = quote.carrier_id || quote.carrier || carrierName;

      if (!carriersMap.has(carrierId)) {
        carriersMap.set(carrierId, {
          id: carrierId,
          name: carrierName,
          count: 0
        });
      }
      carriersMap.get(carrierId).count++;
    });

    return Array.from(carriersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  /**
   * Get current form carriers filtered by available carriers
   */
  const getCurrentFormCarriers = useCallback(() => {
    const availableCarriers = getAvailableCarriers();
    const availableCarrierIds = new Set(availableCarriers.map(c => String(c.id)));

    return (formData.selectedCarriers || []).filter(carrierId => availableCarrierIds.has(String(carrierId)));
  }, [formData.selectedCarriers, getAvailableCarriers]);

  /**
   * Reset life carriers to select all
   */
  const resetLifeCarriersToAll = useCallback(() => {
    if (!Array.isArray(alqCarriers) || !alqCarriers.length) return;

    const allCarrierIds = alqCarriers.map(c => c.id);
    if (updateFormField) {
      updateFormField('selectedCarriers', allCarrierIds);
    }
    setHasUserEditedLifeCarriers(false);
  }, [alqCarriers, updateFormField]);

  /**
   * Update selected carriers
   */
  const updateSelectedCarriers = useCallback(
    carriers => {
      if (updateFormField) {
        updateFormField('selectedCarriers', carriers);
      }

      // Mark as user-edited if on life tab
      if (activeTab === 'new-life') {
        setHasUserEditedLifeCarriers(true);
      }

      // Mark as manual edit for annuity
      if (activeTab === 'new-annuity') {
        manualAnnuityEditRef.current = true;
        if (carriers.length === 0) {
          setUserClearedAnnuity(true);
        }
      }
    },
    [activeTab, updateFormField]
  );

  /**
   * Clear carrier selection
   */
  const clearCarriers = useCallback(() => {
    if (updateFormField) {
      updateFormField('selectedCarriers', []);
    }

    if (activeTab === 'new-life') {
      setHasUserEditedLifeCarriers(true);
    }

    if (activeTab === 'new-annuity') {
      setUserClearedAnnuity(true);
      manualAnnuityEditRef.current = true;
    }
  }, [activeTab, updateFormField]);

  /**
   * Auto-select all carriers for annuity on first load
   */
  useEffect(() => {
    if (
      activeTab === 'new-annuity' &&
      !hasAutoSelectedAnnuity &&
      !userClearedAnnuity &&
      !manualAnnuityEditRef.current &&
      Array.isArray(aaqCarriers) &&
      aaqCarriers.length > 0 &&
      (!formData.selectedCarriers || formData.selectedCarriers.length === 0)
    ) {
      const allCarrierIds = aaqCarriers.map(c => c.id);
      if (updateFormField) {
        updateFormField('selectedCarriers', allCarrierIds);
      }
      setHasAutoSelectedAnnuity(true);
    }
  }, [activeTab, aaqCarriers, formData.selectedCarriers, hasAutoSelectedAnnuity, userClearedAnnuity, updateFormField]);

  /**
   * Reset state when tab changes
   */
  const resetTabState = useCallback(() => {
    setHasAutoSelectedAnnuity(false);
    setUserClearedAnnuity(false);
    setHasUserEditedLifeCarriers(false);
    manualAnnuityEditRef.current = false;
  }, []);

  /**
   * Compare two carrier arrays for equality
   */
  const areCarriersEqual = useCallback((arr1, arr2) => {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
      return false;
    }

    if (arr1.length !== arr2.length) {
      return false;
    }

    const set1 = new Set(arr1.map(c => `${c.value || c}|${c.name || c}`));
    const set2 = new Set(arr2.map(c => `${c.value || c}|${c.name || c}`));

    return set1.size === set2.size && [...set1].every(item => set2.has(item));
  }, []);

  return {
    // State
    hasUserEditedLifeCarriers,
    hasAutoSelectedAnnuity,
    userClearedAnnuity,

    // Getters
    getAvailableCarriers,
    getAvailableCarriersFromQuotes,
    getCurrentFormCarriers,

    // Actions
    updateSelectedCarriers,
    clearCarriers,
    resetLifeCarriersToAll,
    resetTabState,
    areCarriersEqual,

    // State setters for direct access
    setHasUserEditedLifeCarriers,
    setHasAutoSelectedAnnuity,
    setUserClearedAnnuity
  };
};

export default useCarrierSelection;
